import { useSelector } from "react-redux";
import { upperFirst, lowerCase } from "lodash";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import { type ApplicationFormSlice, type ApplicationState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import { getFieldsCustomLabel } from "./utils/getFieldsCustomLabel";
import { getInvalidFieldAndMessage } from "./utils/conditionalFields";

interface RequiredFieldsLabel {
  fieldName: string;
  fieldLabel: string;
  requiredMessage: string;
}

const stripIdentifierSuffix = (field: string): string => field.replace(/_[a-f0-9]{24}(?=(_isInvalid)?$)/i, "");

const useRequiredFieldsLabel = () => {
  const { applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);

  const getSimpleFieldsLabel = (remainingFields: string[]): RequiredFieldsLabel[] => {
    const fieldsDefaultLabel = remainingFields.map((field) => {
      const displayField = stripIdentifierSuffix(field);
      const fieldLabel = upperFirst(lowerCase(displayField));
      const isInvalid = regex.invalid.test(fieldLabel);
      const { fieldName: rawFieldName, customValidationMessage } = getInvalidFieldAndMessage(field);
      const fieldName = stripIdentifierSuffix(rawFieldName);
      const fieldLabelContainsIsInvalid = fieldLabel.toLowerCase().includes("is invalid");
      // customValidationMessage takes precedence, else if the label already contains the word "is invalid", we don't need to add it again.
      const invalidMessage = customValidationMessage
        ? customValidationMessage
        : fieldLabelContainsIsInvalid
          ? fieldLabel
          : `${fieldLabel} is invalid`;
      return {
        fieldName,
        fieldLabel: isInvalid ? invalidMessage : fieldLabel,
        requiredMessage: isInvalid ? invalidMessage : `${fieldLabel} is required`,
      };
    });
    const fieldsCustomLabel = getFieldsCustomLabel(applicationType);
    const fieldsLabel = fieldsDefaultLabel.map((field) => {
      const customLabel = fieldsCustomLabel[field.fieldName];
      if (customLabel) {
        return {
          fieldName: field.fieldName,
          fieldLabel: customLabel,
          requiredMessage: `${customLabel} is required`,
        };
      }
      return field;
    });
    return fieldsLabel;
  };

  const getComplexFieldsLabel = (remainingFields: string[]): RequiredFieldsLabel[] => {
    const sanitisedFields = remainingFields.map((field) => {
      if (field.startsWith("employerRequired_")) {
        return {
          fieldName: field,
          fieldLabel: "At least one employer",
          requiredMessage: "At least one employer is required.",
        };
      }
      const fieldParts = field.split("_");
      const isInvalid = regex.invalid.test(field);
      const regexWithIndexMarker = /\[\d+\]/;
      const fieldPartsContainsIndexMarker = fieldParts.some((item) => regexWithIndexMarker.test(item));
      if (fieldPartsContainsIndexMarker) {
        const filteredFieldParts = fieldParts[0].split(".").map((item) => {
          return item
            .replace(regexWithIndexMarker, "")
            .replace(/slice\(-1\)/g, "")
            .replace("?", "");
        });
        const removedEmptyStrings = filteredFieldParts.filter((item) => item !== "");
        const sanitisedFields = removedEmptyStrings.map((field) => lowerCase(field));
        const fieldLabel = upperFirst(sanitisedFields.join(" "));
        const { fieldName, customValidationMessage } = getInvalidFieldAndMessage(field);
        const fieldLabelContainsIsInvalid = fieldLabel.toLowerCase().includes("is invalid");
        // customValidationMessage takes precedence, else if the label already contains the word "is invalid", we don't need to add it again.
        const invalidMessage = customValidationMessage
          ? customValidationMessage
          : fieldLabelContainsIsInvalid
            ? fieldLabel
            : `${fieldLabel} is invalid`;

        return {
          fieldName,
          fieldLabel: isInvalid ? invalidMessage : fieldLabel,
          requiredMessage: isInvalid ? invalidMessage : `${fieldLabel} is required`,
        };
      } else {
        const filteredFieldParts = fieldParts.filter((item) => !/\d/.test(item) || item === "addressLine2");
        const reversedFieldParts = filteredFieldParts.reverse();
        const sanitisedFields = reversedFieldParts.map((field) => lowerCase(field));
        const fieldLabel = upperFirst(sanitisedFields.join(" "));
        const { fieldName, customValidationMessage } = getInvalidFieldAndMessage(field);
        const fieldLabelContainsIsInvalid = fieldLabel.toLowerCase().includes("is invalid");
        // customValidationMessage takes precedence, else if the label already contains the word "is invalid", we don't need to add it again.
        const invalidMessage = customValidationMessage
          ? customValidationMessage
          : fieldLabelContainsIsInvalid
            ? fieldLabel
            : `${fieldLabel} is invalid`;

        return {
          fieldName,
          fieldLabel: isInvalid ? invalidMessage : fieldLabel,
          requiredMessage: isInvalid ? invalidMessage : `${fieldLabel} is required`,
        };
      }
    });
    const deduplicatedFields = sanitisedFields.filter(
      (field, index, list) =>
        list.findIndex((t) =>
          field.fieldName.startsWith("employerRequired_")
            ? t.fieldName === field.fieldName
            : t.fieldLabel === field.fieldLabel,
        ) === index,
    );
    const fieldsCustomLabel = getFieldsCustomLabel(applicationType);
    const fieldsLabel = deduplicatedFields.map((field) => {
      const customLabel = fieldsCustomLabel[field.fieldName];
      if (customLabel) {
        return {
          fieldName: field.fieldName,
          fieldLabel: customLabel,
          requiredMessage: `${customLabel} is required`,
        };
      }
      return field;
    });
    return fieldsLabel;
  };

  return {
    getSimpleFieldsLabel,
    getComplexFieldsLabel,
  };
};

export { useRequiredFieldsLabel };
export type { RequiredFieldsLabel };
